<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useCompoundStore } from '@/stores/compound';
import { useOptionsStore } from '@/stores/options';

// --- Props and Emits ---
const props = defineProps({
    compound: {
        type: Object,
        default: () => ({
            name: null,
            is_active: true,
            is_sterile: false,
            compound_type_id: 1,
            unit_id: 1,
            products: [],
            bulk_pricing_rules: [],
        }),
    },
    mode: {
        type: String,
        default: 'Create',
    },
});
const emit = defineEmits(['close-modal']);

// --- Stores ---
const { store, update } = useCompoundStore();
const optionsStore = useOptionsStore();
const { compoundTypeOptions, labProducts, unitOptions, pricingSchemeOptions } = storeToRefs(optionsStore);

// --- Constants ---
const PRODUCT_CATEGORY_MAP = {
    base: 'baseProducts',
    active: 'activeIngredientProducts',
    supply: 'supplyProducts',
};

// --- Form Initialization ---
const form = ref({
    ...props.compound,
    baseProducts: [],
    activeIngredientProducts: [],
    supplyProducts: [],
});

// --- Helpers ---

// Determine product category from its type name (default is "active")
const getProductCategory = (product) => {
    const typeName = product?.type?.name?.toLowerCase() || '';
    if (typeName.includes('base')) {
        return 'base';
    }
    if (typeName.includes('supply')) {
        return 'supply';
    }
    return 'active';
};

// Distribute existing products into the corresponding arrays
if (props.compound.products?.length) {
    props.compound.products.forEach((p) => {
        const category = getProductCategory(p);

        form.value[PRODUCT_CATEGORY_MAP[category]].push({
            product_id: p.id,
            percentage: p.pivot?.percentage || 0,
            quantity: p.pivot?.quantity || 0,
        });
    });
}

// Get all selected product IDs across categories
const getSelectedProductIds = () => {
    return [
        ...form.value.baseProducts.map((p) => p.product_id).filter((id) => id != null),
        ...form.value.activeIngredientProducts.map((p) => p.product_id).filter((id) => id != null),
        ...form.value.supplyProducts.map((p) => p.product_id).filter((id) => id != null),
    ];
};

// Filter available product options for a given category,
// excluding options already selected (unless it is the current selection),
// and matching the selected compound type
const getFilteredOptions = (category, currentProductId) => {
    const optionsByCategory = labProducts.value.filter(option =>
        option.type.toLowerCase() === category &&
        option.compound_types.includes(form.value.compound_type_id)
    );
    const selectedIds = getSelectedProductIds();
    return optionsByCategory.filter(option =>
        option.value === currentProductId || !selectedIds.includes(option.value)
    );
};

// Add a new product row for the given category
const addProduct = (category) => {
    const key = PRODUCT_CATEGORY_MAP[category];
    if (key) {
        form.value[key].push({ product_id: null, percentage: 0, quantity: 0 });
    }
};

// Remove a product row by index for the given category
const removeProduct = (category, index) => {
    const key = PRODUCT_CATEGORY_MAP[category];
    if (key) {
        form.value[key].splice(index, 1);
    }
};

// Save the form by merging the separate product arrays and sending the payload.
const save = async () => {
    const combinedProducts = [
        ...form.value.baseProducts,
        ...form.value.activeIngredientProducts,
        ...form.value.supplyProducts,
    ];
    
    const payload = {
        ...form.value,
        products: combinedProducts.map(p => {
            const product_obj = {
                id: p.product_id,
                pivot: {
                    percentage: p.percentage,
                    quantity: p.quantity,
                },
            };

            return product_obj; 
        })
    };

    const action = props.mode === 'Create' ? store : update;
    await action(payload);
    emit('close-modal');
};

const clearProducts = () => {
    form.value.baseProducts = [];
    form.value.activeIngredientProducts = [];
    form.value.supplyProducts = [];
};

// Add a new bulk pricing rule row
const addBulkPricingRule = () => {
    form.value.bulk_pricing_rules.push({ threshold: null, unit_price: null });
};

// Remove a bulk pricing rule row by index
const removeBulkPricingRule = (index) => {
    form.value.bulk_pricing_rules.splice(index, 1);
};
</script>

<template>
<ModalWrapper>
    <template #header>
        {{ $t("label.mode_what", { mode: mode, what: "compound" }) }}
    </template>
    <template #subheader>
        {{ $t("label.create_or_edit", { what: "compound" }) }}
    </template>

    <form @submit.prevent="save">
        <div class="md:grid md:grid-cols-3 gap-x-4">
            <StyledInput
                v-model="form.name"
                name="name"
                :label="$t('label.name')"
                :placeholder="$t('label.name')" />

            <StyledDropdown
                v-model="form.compound_type_id"
                :clearable="false"
                :label="$t('label.compound_type')"
                :placeholder="$t('label.compound_type')"
                :options="compoundTypeOptions"
                name="compound_type_id"
                @input="clearProducts()" />

            <StyledDropdown
                v-model="form.unit_id"
                :clearable="false"
                :label="$t('label.unit')"
                :placeholder="$t('label.unit')"
                :options="unitOptions"
                name="unit_id" />

            <StyledDropdown
                v-model="form.is_active"
                :clearable="false"
                :label="$t('label.status')"
                :placeholder="$t('label.status')"
                :options="[
                    { label: $t('label.active'), value: true },
                    { label: $t('label.inactive'), value: false }
                ]"
                name="is_active" />

            <StyledInput
                v-model="form.expiry"
                name="expiry"
                :label="$t('label.expiry_days')"
                :placeholder="$t('label.expiry')" />

            <StyledDropdown
                v-model="form.pricing_scheme_id"
                :clearable="false"
                :label="$t('label.pricing_scheme')"
                :placeholder="$t('label.pricing_scheme')"
                :options="pricingSchemeOptions"
                name="pricing_scheme_id" />

            <v-checkbox
                v-model="form.is_sterile"
                :label="$t('label.is_sterile')" />
        </div>

        <!-- Products Assignment Section -->
        <div>
            <h3 class="text-lg font-bold">{{ $t("label.products") }}</h3>
            <!-- Active Ingredients -->
            <div class="mt-4 flex flex-col gap-4">
                <div class="font-semibold">{{ $t("label.active_ingredients") }}</div>
                <div
                    v-for="(product, index) in form.activeIngredientProducts"
                    :key="'active-' + index"
                    class="flex items-center space-x-4 mt-2">
                    <div class="grid grid-cols-2 gap-4 w-full">
                        <StyledDropdown
                            v-model="product.product_id"
                            :label="$t('label.product')"
                            :placeholder="$t('label.select_product')"
                            :options="getFilteredOptions('active', product.product_id)"
                            :name="`activeIngredientProducts.${index}.product_id`" />
                        <StyledInput
                            v-model="product.percentage"
                            type="number"
                            :name="`activeIngredientProducts.${index}.percentage`"
                            :label="$t('label.percentage')"
                            :placeholder="$t('label.percentage')" />
                    </div>
                    <v-icon color="red" class="self-center" @click="removeProduct('active', index)">
                        mdi-delete-outline
                    </v-icon>
                </div>
                <StyledButton name="add" @click="addProduct('active')">
                    {{ $t("label.add_active_ingredient") }}
                </StyledButton>
            </div>
            
            <!-- Base Products -->
            <div class="mt-4 flex flex-col gap-4">
                <div class="font-semibold">{{ $t("label.base_products") }}</div>
                <div
                    v-for="(product, index) in form.baseProducts"
                    :key="'base-' + index"
                    class="flex items-center space-x-4 mt-2">
                    <div class="grid grid-cols-2 gap-4 w-full">
                        <StyledDropdown
                            v-model="product.product_id"
                            :placeholder="$t('label.select_product')"
                            :options="getFilteredOptions('base', product.product_id)"
                            :name="`baseProducts.${index}.product_id`" />
                        <StyledInput
                            v-model="product.percentage"
                            type="number"
                            :label="$t('label.percentage')"
                            :placeholder="$t('label.percentage')"
                            :name="`baseProducts.${index}.percentage`" />
                    </div>
                    <v-icon color="red" class="self-center" @click="removeProduct('base', index)">
                        mdi-delete-outline
                    </v-icon>
                </div>
                <StyledButton name="add" @click="addProduct('base')">
                    {{ $t("label.add_base_product") }}
                </StyledButton>
            </div>

            <!-- Supplies -->
            <div class="mt-4 flex flex-col gap-4">
                <div class="font-semibold">{{ $t("label.supplies") }}</div>
                <div
                    v-for="(product, index) in form.supplyProducts"
                    :key="'supply-' + index"
                    class="flex items-center space-x-4 mt-2">
                    <div class="grid grid-cols-2 gap-4 w-full">
                        <StyledDropdown
                            v-model="product.product_id"
                            :placeholder="$t('label.select_product')"
                            :options="getFilteredOptions('supply', product.product_id)"
                            :name="`supplyProducts.${index}.product_id`" />
                        <StyledInput
                            v-model="product.quantity"
                            type="number"
                            :name="`supplyProducts.${index}.quantity`"
                            :label="$t('label.quantity')"
                            :placeholder="$t('label.quantity')" />
                    </div>
                    <v-icon color="red" class="self-center" @click="removeProduct('supply', index)">
                        mdi-delete-outline
                    </v-icon>
                </div>
                <StyledButton name="add" @click="addProduct('supply')">
                    {{ $t("label.add_supply") }}
                </StyledButton>
            </div>

            <div class="mt-4 flex flex-col gap-4">
                <div class="font-semibold">{{ $t("label.bulk_pricing_rules") }}</div>
                <div
                    v-for="(rule, index) in form.bulk_pricing_rules"
                    :key="'bulk-rule-' + index"
                    class="flex items-center space-x-4 mt-2">
                    <div class="grid grid-cols-2 gap-4 w-full">
                        <StyledInput
                            v-model="rule.unit_threshold"
                            type="number"
                            :label="$t('label.unit_threshold')"
                            :placeholder="$t('label.unit_threshold')"
                            :name="`bulk_pricing_rules.${index}.unit_threshold`" />
                        <StyledInput
                            v-model="rule.bulk_unit_price"
                            type="number"
                            :label="$t('label.bulk_unit_price')"
                            :placeholder="$t('label.bulk_unit_price')"
                            :name="`bulk_pricing_rules.${index}.bulk_unit_price`" />
                    </div>
                    <v-icon color="red" class="self-center" @click="removeBulkPricingRule(index)">
                        mdi-delete-outline
                    </v-icon>
                </div>
                <StyledButton name="add" @click="addBulkPricingRule">
                    {{ $t("label.add_rule") }}
                </StyledButton>
            </div>
        </div>
    </form>

    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
